import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { api } from "../lib/api";
import { useT } from "../lib/i18n";
import { useSeo } from "../lib/useSeo";
import { ListingCard, ListingCardSkeleton } from "../components/ListingCard";
import { HeroSection } from "../components/home/HeroSection";
import { FeatureShowcase } from "../components/FeatureShowcase";
import { ScamCheckDemo } from "../components/ScamCheckDemo";
import { SocialProof } from "../components/SocialProof";
import { FAQ } from "../components/FAQ";
import { Button } from "../components/ui/button";
import LeadCta from "../components/leads/LeadCta";

const CITIES = [
  { name: "Amsterdam", img: "https://images.unsplash.com/photo-1519475889208-0968e5438f7d" },
  { name: "Rotterdam", img: "https://images.pexels.com/photos/36098026/pexels-photo-36098026.jpeg" },
  { name: "Utrecht", img: "https://images.unsplash.com/photo-1621189287244-114a2080d716" },
  { name: "Den Haag", img: "https://images.unsplash.com/photo-1700602079016-6042d1b933b6" },
  { name: "Eindhoven", img: "https://images.pexels.com/photos/34002112/pexels-photo-34002112.jpeg" },
];

const Home = () => {
  const { t } = useT();
  const [listings, setListings] = useState(null);

  useSeo({ title: t("seo.home.title"), description: t("seo.home.desc"), image: "/og-image.png", path: "/", exactTitle: true });

  useEffect(() => {
    api("/listings?sort=match_desc")
      .then((d) => {
        const seen = new Set();
        const unique = (d.listings || []).filter((l) => (seen.has(l.id) ? false : seen.add(l.id)));
        setListings(unique.slice(0, 6));
      })
      .catch(() => setListings([]));
  }, []);

  return (
    <div>
      {/* Hero */}
      <HeroSection />

      {/* Verhuur / verkoop je woning CTA — direct onder de hero zodat eigenaren weten dat ze ook kunnen plaatsen */}
      <section className="mx-auto max-w-6xl px-4 pt-8 sm:px-6" data-testid="owner-cta">
        <div className="grid gap-4 sm:grid-cols-2">
          {/* Particulier */}
          <div className="rounded-3xl bg-gradient-to-br from-orange-500 to-orange-600 p-5 text-white shadow-md sm:p-6">
            <div className="text-xs font-bold uppercase tracking-wide text-white/80">Particulier · gratis</div>
            <h3 className="mt-1 font-display text-xl font-extrabold">Verkoop zelf je woning</h3>
            <p className="mt-1 text-sm text-white/90">Zonder bemiddelingskosten. Max. 1 actieve advertentie.</p>
            <div className="mt-4">
              <Link
                to="/mijn-woning-verkopen"
                data-testid="owner-cta-sell-particulier"
                className="group flex items-center justify-between gap-2 rounded-2xl bg-white/15 px-4 py-3 text-sm font-bold text-white backdrop-blur transition hover:bg-white/25"
              >
                Verkopen <ArrowRight size={16} className="transition-transform group-hover:translate-x-0.5" />
              </Link>
            </div>
          </div>

          {/* Makelaar */}
          <div className="rounded-3xl bg-navy p-5 text-white shadow-md sm:p-6">
            <div className="text-xs font-bold uppercase tracking-wide text-amber-300">Voor makelaars</div>
            <h3 className="mt-1 font-display text-xl font-extrabold">Plaats een huur- of koopwoning</h3>
            <p className="mt-1 text-sm text-white/80">Bereik duizenden woningzoekers via WoningAlert.</p>
            <div className="mt-4 grid gap-2 sm:grid-cols-2">
              <Link
                to="/koop/makelaar/wizard"
                data-testid="owner-cta-sell-makelaar"
                className="group flex items-center justify-between gap-2 rounded-2xl bg-white/10 px-4 py-3 text-sm font-bold text-white transition hover:bg-white/20"
              >
                Verkopen <ArrowRight size={16} className="text-amber-300 transition-transform group-hover:translate-x-0.5" />
              </Link>
              <Link
                to="/makelaar/woning/nieuw"
                data-testid="owner-cta-rent-makelaar"
                className="group flex items-center justify-between gap-2 rounded-2xl bg-white/10 px-4 py-3 text-sm font-bold text-white transition hover:bg-white/20"
              >
                Verhuren <ArrowRight size={16} className="text-amber-300 transition-transform group-hover:translate-x-0.5" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Feature showcase with in-app previews */}
      <FeatureShowcase />

      {/* Scam-check demo */}
      <ScamCheckDemo />

      {/* Social proof */}
      <SocialProof />

      {/* Cities */}
      <section className="mx-auto max-w-6xl px-4 sm:px-6">
        <h2 className="font-display text-2xl font-extrabold text-navy">{t("home.cities.title")}</h2>
        <div className="no-scrollbar mt-6 flex gap-4 overflow-x-auto pb-2">
          {CITIES.map((c) => (
            <Link
              key={c.name}
              to={`/woningen?city=${encodeURIComponent(c.name)}`}
              data-testid={`city-${c.name}`}
              className="group relative h-44 w-64 shrink-0 overflow-hidden rounded-3xl"
            >
              <img src={c.img} alt={c.name} className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-110" />
              <div className="scrim-bottom absolute inset-0" />
              <span className="absolute bottom-4 left-4 font-display text-xl font-bold text-white">{c.name}</span>
            </Link>
          ))}
        </div>
      </section>

      {/* Recommended */}
      <section className="mx-auto max-w-4xl px-4 py-16 sm:px-6">
        <div className="flex items-center justify-between">
          <h2 className="font-display text-2xl font-extrabold text-navy">{t("home.recommended.title")}</h2>
          <Link to="/woningen" className="flex items-center gap-1 text-sm font-semibold text-brandblue">
            {t("common.viewAll")} <ArrowRight size={15} />
          </Link>
        </div>
        <div className="mt-6 grid gap-5">
          {listings === null
            ? Array.from({ length: 3 }).map((_, i) => <ListingCardSkeleton key={i} />)
            : listings.map((l) => <ListingCard key={l.id} listing={l} />)}
        </div>
      </section>

      {/* FAQ */}
      <FAQ />

      {/* Seller / valuation CTA — secondary commercial hook */}
      <section className="mx-auto max-w-6xl px-4 pb-8 sm:px-6" data-testid="home-seller-cta">
        <LeadCta type="sale" variant="banner" />
      </section>

      {/* Mortgage lead CTA */}
      <section className="mx-auto max-w-6xl px-4 pb-8 sm:px-6">
        <LeadCta type="mortgage" variant="banner" />
      </section>

      {/* CTA */}
      <section className="mx-auto max-w-6xl px-4 pb-8 sm:px-6">
        <div className="overflow-hidden rounded-[2rem] bg-gradient-to-br from-orange-500 to-orange-600 p-10 text-center shadow-md sm:p-16">
          <h2 className="font-display text-3xl font-extrabold text-white">{t("home.cta.title")}</h2>
          <p className="mx-auto mt-3 max-w-xl text-white/90">{t("home.cta.desc")}</p>
          <Button asChild className="mt-8 rounded-full bg-white px-8 text-orange-600 hover:bg-white/90">
            <Link to="/register" data-testid="cta-register-btn">{t("home.hero.cta2")}</Link>
          </Button>
        </div>
      </section>
    </div>
  );
};

export default Home;
